<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";
import WrIcon from "../global/WrIcon.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface CtaCard {
  title: string;
  description: string;
  svgIcon?: string;
}

const emit = defineEmits<{ "cta-clicked": [] }>();

withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    items: CtaCard[];
    eyebrow?: string;
    ctaText?: string;
    ctaLink?: string | null;
  }>(),
  {
    eyebrow: "Get started",
    ctaText: "Contact us",
    ctaLink: null,
  },
);
</script>

<template>
  <section class="relative">
    <div
      class="relative overflow-hidden rounded-3xl border px-5 py-10 lg:px-10 lg:py-14"
        :class="isLight ? 'border-[rgba(208,208,208,0.5)] bg-white shadow-[0_2px_12px_rgba(0,0,0,0.05)]' : 'border-[rgba(136,127,255,0.2)] bg-[rgba(18,0,44,0.5)]'"
      >
        <div class="fl-cta-glow" />

        <div class="text-center">
          <span class="fl-eyebrow"><span class="fl-eyebrow-dot" />{{ eyebrow }}</span>
          <h2 class="fl-heading">{{ heading }}</h2>
          <p class="fl-subcopy" :class="isLight ? 'text-black opacity-70' : 'text-white opacity-70'">{{ subcopy }}</p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 mt-9 lg:mt-12">
          <div v-for="item in items" :key="item.title" class="fl-card group">
              <div class="fl-icon">
                <WrIcon :name="item.svgIcon || 'streamline:help-question-1'" class="size-6" :class="isLight ? 'text-[#501D71]' : 'text-[#BA55FE]'" />
              </div>
              <h3 class="fl-card-title">{{ item.title }}</h3>
            <p class="fl-card-desc">{{ item.description }}</p>
          </div>
        </div>

        <div class="flex justify-center mt-9 lg:mt-12">
          <WrButton
            :link="ctaLink || undefined"
            @click="!ctaLink ? emit('cta-clicked') : undefined"
            container-class="h-[48px] lg:h-[52px]"
            append-class="text-[15px] lg:font-normal px-6 lg:px-7"
          >
            {{ ctaText }}
          </WrButton>
        </div>
    </div>
  </section>
</template>

<style scoped>
.fl-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.2em;
  color: #887fff; margin-bottom: 14px;
}
.fl-eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: #ba55fe; box-shadow: 0 0 8px rgba(186, 85, 254, 0.8);
  animation: fl-blink 2.4s ease-in-out infinite;
}
@keyframes fl-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
@media (min-width: 1024px) { .fl-eyebrow { font-size: 12px; } }

.fl-heading {
  font-family: "ITC Avant Garde Gothic Demi", "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: clamp(26px, 3.6vw, 40px); font-weight: 600; line-height: 1.1; text-align: center;
  background: linear-gradient(92deg, #12dbff -3.57%, #ba55fe 80.72%);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; color: transparent;
}
.fl-subcopy {
  font-family: "ITC Avant Garde Gothic", "AvantGarde", sans-serif;
  font-size: 16px; line-height: 1.6; letter-spacing: 0.36px; margin-top: 16px;
  text-align: center; max-width: 720px; margin-left: auto; margin-right: auto; padding: 0 16px;
}
@media (min-width: 1024px) { .fl-subcopy { font-size: 18px; } }

.fl-cta-glow {
  position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(ellipse 600px 300px at 50% 0%, rgba(186, 85, 254, 0.16), transparent 70%);
  animation: fl-glow-pulse 4s ease-in-out infinite;
}
@keyframes fl-glow-pulse { 0%, 100% { opacity: 0.7; } 50% { opacity: 1; } }

.fl-card {
  position: relative; overflow: hidden; height: 100%;
  border-radius: 16px; border: 1px solid rgba(136, 127, 255, 0.15);
  background: rgba(18, 0, 44, 0.4); padding: 20px;
  transition: transform 0.35s ease, border-color 0.35s ease, background 0.35s ease, box-shadow 0.35s ease;
}
.fl-card:hover {
  transform: translateY(-4px);
  border-color: rgba(186, 85, 254, 0.4);
  background: rgba(18, 0, 44, 0.6);
  box-shadow: 0 24px 44px -22px rgba(186, 85, 254, 0.45);
}
.fl-card::after {
  content: ""; position: absolute; inset: 0;
  background: linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.09) 50%, transparent 60%);
  transform: translateX(-130%); transition: transform 0.75s ease; pointer-events: none;
}
.fl-card:hover::after { transform: translateX(130%); }

.fl-icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center; margin-bottom: 16px;
  border: 1px solid rgba(186, 85, 254, 0.25); background: rgba(186, 85, 254, 0.08);
  transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
}
.fl-card:hover .fl-icon {
  transform: scale(1.08) rotate(-4deg);
  border-color: rgba(186, 85, 254, 0.5);
  box-shadow: 0 0 20px rgba(186, 85, 254, 0.28);
}
.fl-card-title { font-size: 16px; font-weight: 600; color: #fff; margin-bottom: 6px; }
@media (min-width: 1024px) { .fl-card-title { font-size: 17px; } }
.fl-card-desc { font-size: 13px; line-height: 1.6; color: #bcb2c3; }

/* Light theme */
html:not(.my-app-dark) .fl-eyebrow { color: #613faa; }
html:not(.my-app-dark) .fl-card {
  background: #f6f4fb;
  border-color: rgba(208, 208, 208, 0.6);
}
html:not(.my-app-dark) .fl-card:hover {
  background: #fff;
  border-color: rgba(186, 85, 254, 0.35);
  box-shadow: 0 8px 24px rgba(166, 85, 246, 0.15);
}
html:not(.my-app-dark) .fl-icon {
  background: linear-gradient(135deg, rgba(18, 219, 255, 0.15) 0%, rgba(186, 85, 254, 0.2) 100%);
  border-color: transparent;
}
html:not(.my-app-dark) .fl-card:hover .fl-icon { border-color: transparent; box-shadow: 0 0 16px rgba(166, 85, 246, 0.25); }
html:not(.my-app-dark) .fl-card-title { color: #613faa; }
html:not(.my-app-dark) .fl-card-desc { color: #333; }
</style>
